import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import {
  driverService,
  vehicleService,
  accountService,
  kycService,
  KycSubjectType,
  DocumentAnalysisResult,
  VerificationRequestDto,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { PropertyView, PropertySection, PropertyField } from './PropertyView'
import SubjectDetailPanel, {
  SubjectStatusChips,
  getSubjectDisplayName,
} from './SubjectDetailPanel'

type Mode = 'review' | 'initiate'

export interface VerificationReviewDialogProps {
  open: boolean
  onClose: () => void
  /** Existing verification id — used in review mode. */
  verificationId?: string
  /** Row data from the pending list — used when getById is unavailable. */
  verificationHint?: VerificationRequestDto
  /** Initiate-mode params (used when no verification exists yet). */
  initiate?: {
    subjectType: KycSubjectType
    subjectId: string
    accountId: string
  }
  onActionComplete?: () => void
}

export default function VerificationReviewDialog({
  open,
  onClose,
  verificationId,
  verificationHint,
  initiate,
  onActionComplete,
}: VerificationReviewDialogProps) {
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()
  const mode: Mode = verificationId ? 'review' : 'initiate'

  const [notes, setNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)
  const [aiResult, setAiResult] = useState<DocumentAnalysisResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setNotes('')
      setRejectionReason('')
      setShowRejectInput(false)
      setAiResult(null)
      setError(null)
      setSuccess(null)
    }
  }, [open])

  // Load verification details (review mode)
  const {
    data: verification,
    isLoading: loadingVerification,
    isError: verificationLoadError,
  } = useQuery({
    queryKey: ['kyc-verification', verificationId],
    queryFn: () => kycService.getById(verificationId as string),
    enabled: open && mode === 'review' && !!verificationId,
  })

  const effectiveVerification = verification ?? verificationHint

  const subjectType: KycSubjectType | undefined =
    effectiveVerification?.subjectType || initiate?.subjectType
  const subjectId: string | undefined =
    effectiveVerification?.subjectId || initiate?.subjectId
  const accountId: string | undefined =
    effectiveVerification?.accountId || initiate?.accountId

  // Resolve subject + account names
  const { data: driverSubject, isLoading: loadingDriver } = useQuery({
    queryKey: ['driver', subjectId],
    queryFn: () => driverService.getById(subjectId as string),
    enabled: open && subjectType === 'DRIVER' && !!subjectId,
  })
  const { data: vehicleSubject, isLoading: loadingVehicle } = useQuery({
    queryKey: ['vehicle', subjectId],
    queryFn: () => vehicleService.getById(subjectId as string),
    enabled: open && subjectType === 'VEHICLE' && !!subjectId,
  })
  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountService.getById(accountId as string),
    enabled: open && !!accountId,
  })

  const subjectName = useMemo(
    () => getSubjectDisplayName(subjectType, driverSubject, vehicleSubject, subjectId),
    [subjectType, driverSubject, vehicleSubject, subjectId]
  )

  const accountName =
    (account as { name?: string; businessName?: string })?.name ||
    (account as { businessName?: string })?.businessName ||
    ''

  const dialogTitle = useMemo(() => {
    if (mode === 'initiate') return 'Initiate KYC'
    if (subjectType === 'DRIVER') {
      return `Driver Details${subjectName ? ` — ${subjectName}` : ''}`
    }
    if (subjectType === 'VEHICLE') {
      return `Vehicle Details${subjectName ? ` — ${subjectName}` : ''}`
    }
    return 'KYC Verification Review'
  }, [mode, subjectType, subjectName])

  const loadingSubject =
    (subjectType === 'DRIVER' && loadingDriver) ||
    (subjectType === 'VEHICLE' && loadingVehicle)

  // Mutations
  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveVerification) throw new Error('No verification loaded')
      return kycService.verify(effectiveVerification.id, effectiveVerification, notes)
    },
    onSuccess: () => {
      setSuccess('Verification approved')
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
      queryClient.invalidateQueries({ queryKey: ['kyc-verification', verificationId] })
      onActionComplete?.()
      setTimeout(onClose, 600)
    },
    onError: (e: Error) => setError(e.message || 'Failed to verify'),
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveVerification) throw new Error('No verification loaded')
      if (!rejectionReason.trim()) throw new Error('Rejection reason is required')
      return kycService.reject(
        effectiveVerification.id,
        effectiveVerification,
        rejectionReason,
        notes
      )
    },
    onSuccess: () => {
      setSuccess('Verification rejected')
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
      queryClient.invalidateQueries({ queryKey: ['kyc-verification', verificationId] })
      onActionComplete?.()
      setTimeout(onClose, 600)
    },
    onError: (e: Error) => setError(e.message || 'Failed to reject'),
  })

  const saveNotesMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveVerification) throw new Error('No verification loaded')
      return kycService.update(effectiveVerification.id, {
        ...effectiveVerification,
        verificationNotes: notes,
      })
    },
    onSuccess: () => {
      setSuccess('Notes saved')
      queryClient.invalidateQueries({ queryKey: ['kyc-verification', verificationId] })
    },
    onError: (e: Error) => setError(e.message || 'Failed to save notes'),
  })

  const initiateMutation = useMutation({
    mutationFn: async () => {
      if (!initiate) throw new Error('Initiate parameters missing')
      return kycService.initiate(initiate)
    },
    onSuccess: () => {
      setSuccess('Verification initiated')
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
      onActionComplete?.()
      setTimeout(onClose, 800)
    },
    onError: (e: Error) => setError(e.message || 'Failed to initiate'),
  })

  const aiAssessmentMutation = useMutation({
    mutationFn: async () => {
      if (!verificationId) throw new Error('No verification id')
      return kycService.getAiAssessment(verificationId)
    },
    onSuccess: (data) => {
      setAiResult(data)
    },
    onError: (e: Error) => setError(e.message || 'AI assessment failed'),
  })

  const isPending = effectiveVerification?.status === 'PENDING'
  const dueDate = effectiveVerification?.dueDate
    ? new Date(effectiveVerification.dueDate)
    : null
  const isOverdue = dueDate ? dueDate.getTime() < Date.now() && isPending : false

  const statusColor = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'success' as const
      case 'REJECTED':
        return 'error' as const
      case 'EXPIRED':
      case 'SUSPENDED':
        return 'default' as const
      default:
        return 'warning' as const
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {subjectType === 'DRIVER' ? <PersonIcon /> : <LocalShippingIcon />}
          <Box>
            <Typography variant="h6" component="div">
              {dialogTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mode === 'review' ? 'KYC Verification Review' : 'New verification request'}
              {subjectType ? ` · ${getLocalizedLabel('kycSubjectType', subjectType)}` : ''}
              {accountName ? ` · ${accountName}` : ''}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loadingVerification && !verificationHint && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loadingVerification && mode === 'initiate' && (
          <Stack spacing={2}>
            {subjectType && (
              <>
                <SubjectStatusChips
                  subjectType={subjectType}
                  driver={driverSubject}
                  vehicle={vehicleSubject}
                />
                <Divider />
                <SubjectDetailPanel
                  subjectType={subjectType}
                  driver={driverSubject}
                  vehicle={vehicleSubject}
                  isLoading={loadingSubject}
                />
                <Divider />
              </>
            )}
            <Alert severity="info">
              This will create a new KYC verification request for{' '}
              <strong>{subjectName}</strong>. The verification will start in PENDING status.
            </Alert>
          </Stack>
        )}

        {!loadingVerification && mode === 'review' && effectiveVerification && subjectType && (
          <Stack spacing={2}>
            <SubjectStatusChips
              subjectType={subjectType}
              driver={driverSubject}
              vehicle={vehicleSubject}
            />

            <Divider />

            <SubjectDetailPanel
              subjectType={subjectType}
              driver={driverSubject}
              vehicle={vehicleSubject}
              isLoading={loadingSubject}
            />

            {verificationLoadError && !verification && verificationHint && (
              <Alert severity="warning" variant="outlined">
                Could not refresh verification details from the server. Showing data from the
                pending queue.
              </Alert>
            )}

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Verification request
            </Typography>

            {/* Status row */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
              <Chip
                label={getLocalizedLabel(
                  'kycVerificationStatus',
                  effectiveVerification.status
                )}
                color={statusColor(effectiveVerification.status)}
                size="small"
              />
              {effectiveVerification.requiresManualReview && (
                <Chip label="Manual review required" color="warning" size="small" variant="outlined" />
              )}
              {isOverdue && (
                <Chip label="Overdue" color="error" size="small" />
              )}
              {typeof effectiveVerification.autoVerificationScore === 'number' && (
                <Tooltip title="Automated assessment score (0–1)">
                  <Chip
                    label={`AI score: ${effectiveVerification.autoVerificationScore.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Stack>

            <PropertyView>
              <PropertySection>
                <PropertyField
                  label="Verification ID"
                  value={effectiveVerification.id}
                  mono
                  copyable
                  full
                />
                <PropertyField
                  label="Created"
                  value={effectiveVerification.createdAt}
                  type="datetime"
                />
                <PropertyField label="Due date">
                  <Typography variant="body2" color={isOverdue ? 'error' : 'inherit'}>
                    {dueDate ? dueDate.toLocaleString() : '—'}
                  </Typography>
                </PropertyField>
                <PropertyField
                  label="Assigned to"
                  value={effectiveVerification.assignedTo ?? null}
                  mono
                />
                <PropertyField
                  label="Reviewed by"
                  value={effectiveVerification.reviewedBy ?? null}
                  mono
                />
                <PropertyField
                  label="Reviewed at"
                  value={effectiveVerification.reviewedAt}
                  type="datetime"
                />
              </PropertySection>
            </PropertyView>

            {/* Document */}
            {(effectiveVerification.s3Bucket || effectiveVerification.s3Key) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Document
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    s3://{effectiveVerification.s3Bucket}/{effectiveVerification.s3Key}
                  </Typography>
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => aiAssessmentMutation.mutate()}
                      disabled={aiAssessmentMutation.isPending}
                    >
                      {aiAssessmentMutation.isPending ? 'Analyzing…' : 'Run AI Assessment'}
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            {/* AI result */}
            {aiResult && (
              <Alert
                severity={aiResult.isRecognized ? 'info' : 'warning'}
                onClose={() => setAiResult(null)}
              >
                <Typography variant="subtitle2" gutterBottom>
                  AI Analysis Result
                </Typography>
                <Grid container spacing={1}>
                  {aiResult.documentType && (
                    <Grid item xs={6}><strong>Type:</strong> {aiResult.documentType}</Grid>
                  )}
                  {typeof aiResult.confidenceScore !== 'undefined' && (
                    <Grid item xs={6}><strong>Confidence:</strong> {Number(aiResult.confidenceScore).toFixed(2)}</Grid>
                  )}
                  {aiResult.fullName && (
                    <Grid item xs={6}><strong>Name:</strong> {aiResult.fullName}</Grid>
                  )}
                  {aiResult.documentNumber && (
                    <Grid item xs={6}><strong>Doc #:</strong> {aiResult.documentNumber}</Grid>
                  )}
                  {aiResult.plateNumber && (
                    <Grid item xs={6}><strong>Plate:</strong> {aiResult.plateNumber}</Grid>
                  )}
                  {aiResult.expiryDate && (
                    <Grid item xs={6}><strong>Expiry:</strong> {aiResult.expiryDate}</Grid>
                  )}
                  {aiResult.errorMessage && (
                    <Grid item xs={12}><strong>Error:</strong> {aiResult.errorMessage}</Grid>
                  )}
                </Grid>
              </Alert>
            )}

            {/* Existing notes / rejection reason */}
            {effectiveVerification.rejectionReason && (
              <Alert severity="error" variant="outlined">
                <Typography variant="subtitle2">Previous rejection reason</Typography>
                <Typography variant="body2">{effectiveVerification.rejectionReason}</Typography>
              </Alert>
            )}
            {effectiveVerification.verificationNotes && (
              <Alert severity="info" variant="outlined">
                <Typography variant="subtitle2">Previous notes</Typography>
                <Typography variant="body2">{effectiveVerification.verificationNotes}</Typography>
              </Alert>
            )}

            <Divider />

            {/* Editable inputs */}
            <TextField
              fullWidth
              label="Verification notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              placeholder="Optional notes for the audit trail"
            />

            {showRejectInput && (
              <TextField
                fullWidth
                required
                label="Rejection reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                multiline
                rows={2}
                error={!rejectionReason && rejectMutation.isError}
                helperText={!rejectionReason && rejectMutation.isError ? 'Required' : ''}
              />
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success">{success}</Alert>
            )}
          </Stack>
        )}

        {!loadingVerification && mode === 'review' && !effectiveVerification && (
          <Alert severity="error">
            Could not load verification details. Please close and try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>

        {mode === 'initiate' && (
          <Button
            variant="contained"
            onClick={() => initiateMutation.mutate()}
            disabled={initiateMutation.isPending || !initiate}
          >
            {initiateMutation.isPending ? 'Creating…' : 'Initiate Verification'}
          </Button>
        )}

        {mode === 'review' && effectiveVerification && (
          <>
            <Button
              onClick={() => saveNotesMutation.mutate()}
              disabled={saveNotesMutation.isPending || !notes.trim()}
            >
              Save Notes
            </Button>
            {isPending && !showRejectInput && (
              <Button
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setShowRejectInput(true)}
              >
                Reject…
              </Button>
            )}
            {isPending && showRejectInput && (
              <Button
                color="error"
                variant="contained"
                startIcon={<CancelIcon />}
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending || !rejectionReason.trim()}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm Reject'}
              </Button>
            )}
            {isPending && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => verifyMutation.mutate()}
                disabled={verifyMutation.isPending}
              >
                {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
